import {Paper} from '@mantine/core';
import React, {useMemo} from 'react';

import {ProteinCreationSettingsData} from 'antibodies/nested-data-creation/full-screen/ProteinCreationSettingsUtil';
import {buildNdcInterpretationFromRows} from 'antibodies/nested-data-creation/full-screen/ProteinCreationTableUtil';
import {ProteinCreationRow} from 'antibodies/nested-data-creation/full-screen/ProteinCreationTypes';
import {getCountsOfCreatedComponents} from 'antibodies/nested-data-creation/spreadsheet-import/AntibodyNestedDataCreationSpreadsheetImportUtil';
import FinalizePane, {
  FinalizePaneProps,
} from 'antibodies/nested-data-creation/spreadsheet-import/panes/FinalizePane';

interface ProteinCreationFinalizeStepProps extends Pick<
  FinalizePaneProps,
  | 'projectFolderId'
  | 'relatedSchemasConfig'
  | 'translationParameters'
  | 'componentAndRelatedSchemas'
  | 'componentAndRelatedSchemasErrorMessage'
  | 'selectedDocumentId'
> {
  rows: ReadonlyArray<ProteinCreationRow>;
  creationSettings: ProteinCreationSettingsData;
  hasDnaInputs: boolean;
  onSetProjectFolder: (projectFolderId: string) => void;
  onSetRelatedSchemasConfig: (config: FinalizePaneProps['relatedSchemasConfig']) => void;
  onSetTranslationParameters: (params: FinalizePaneProps['translationParameters']) => void;
  onSetSelectedDocumentId: (id: string | null) => void;
}

export default function ProteinCreationFinalizeStep({
  rows,
  creationSettings,
  hasDnaInputs,
  projectFolderId,
  onSetProjectFolder,
  relatedSchemasConfig,
  onSetRelatedSchemasConfig,
  translationParameters,
  onSetTranslationParameters,
  componentAndRelatedSchemas,
  componentAndRelatedSchemasErrorMessage,
  selectedDocumentId,
  onSetSelectedDocumentId,
}: ProteinCreationFinalizeStepProps): JSX.Element {
  const ndcInterpretation = useMemo(
    () =>
      buildNdcInterpretationFromRows(
        rows,
        creationSettings.selectedFormat,
        creationSettings.selectedStartingType
      ),
    [rows, creationSettings.selectedFormat, creationSettings.selectedStartingType]
  );

  const countsOfCreatedComponents = useMemo(
    () =>
      getCountsOfCreatedComponents(
        ndcInterpretation,
        creationSettings.selectedStartingType,
        creationSettings.selectedFormat
      ),
    [ndcInterpretation, creationSettings.selectedStartingType, creationSettings.selectedFormat]
  );

  return (
    <Paper bg="white" radius="md" p="md">
      <FinalizePane
        projectFolderId={projectFolderId}
        setProjectFolder={onSetProjectFolder}
        relatedSchemasConfig={relatedSchemasConfig}
        setRelatedSchemasConfig={onSetRelatedSchemasConfig}
        translationParameters={translationParameters}
        setTranslationParameters={onSetTranslationParameters}
        selectedAntibodyFormat={creationSettings.selectedFormat}
        selectedSchemaName={creationSettings.selectedSchemaName}
        componentAndRelatedSchemas={componentAndRelatedSchemas}
        componentAndRelatedSchemasErrorMessage={componentAndRelatedSchemasErrorMessage}
        selectedDocumentId={selectedDocumentId}
        setSelectedDocumentId={onSetSelectedDocumentId}
        countsOfCreatedComponents={countsOfCreatedComponents}
        renderDnaSchemaSelectors={hasDnaInputs}
      />
    </Paper>
  );
}
